import React, { useState, useEffect } from 'react';
import { api, useAuth, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { 
  X, 
  Sparkles, 
  Bug, 
  Zap,
  Bell,
  ChevronRight
} from 'lucide-react';

const UpdateNotification = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [updates, setUpdates] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only check for updates if user is logged in
    if (user) {
      checkForUpdates();
    }
  }, [user]);

  const checkForUpdates = async () => {
    try {
      const response = await api.get('/updates/latest');
      // Validate response data structure
      if (response.data && 
          response.data.has_unseen && 
          Array.isArray(response.data.updates) && 
          response.data.updates.length > 0) {
        // Filter out any malformed updates
        const validUpdates = response.data.updates.filter(
          u => u && u.type && u.title && u.content
        );
        if (validUpdates.length > 0) {
          setUpdates(validUpdates);
          setVisible(true);
        }
      }
    } catch (error) {
      // Silently fail - updates are not critical
    }
  };

  const handleDismiss = async () => {
    if (updates.length > 0) {
      try {
        // Mark the latest update as seen
        await api.post(`/updates/${updates[0].update_id}/seen`);
      } catch (error) {
        console.error('Error marking update as seen:', error);
      }
    }
    setVisible(false);
  };

  const handleNext = () => {
    if (currentIndex < updates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      handleDismiss();
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'feature':
        return <Sparkles className="w-5 h-5 text-green-500" />;
      case 'bugfix':
        return <Bug className="w-5 h-5 text-red-500" />;
      case 'improvement':
        return <Zap className="w-5 h-5 text-yellow-500" />;
      default:
        return <Bell className="w-5 h-5 text-primary" />;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      feature: language === 'fr' ? 'Nouvelle fonctionnalité' : 'New Feature',
      bugfix: language === 'fr' ? 'Correction de bug' : 'Bug Fix',
      improvement: language === 'fr' ? 'Amélioration' : 'Improvement'
    };
    return labels[type] || (language === 'fr' ? 'Mise à jour' : 'Update');
  };

  const getTypeBg = (type) => {
    switch(type) {
      case 'feature':
        return 'bg-green-500/10 border-green-500/30';
      case 'bugfix':
        return 'bg-red-500/10 border-red-500/30';
      case 'improvement':
        return 'bg-yellow-500/10 border-yellow-500/30';
      default:
        return 'bg-primary/10 border-primary/30';
    }
  };

  if (!visible || updates.length === 0) return null;

  const currentUpdate = updates[currentIndex];
  
  // Safety check - if currentUpdate is undefined or missing required fields, don't render
  if (!currentUpdate || !currentUpdate.type) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="update-notification">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className={`px-6 py-4 border-b ${getTypeBg(currentUpdate.type)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTypeIcon(currentUpdate.type)}
              <span className="font-heading text-sm uppercase tracking-wider text-foreground">
                {getTypeLabel(currentUpdate.type)}
              </span>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={handleDismiss}
              data-testid="close-update-btn"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <h3 className="font-heading text-xl text-foreground mb-3">
            {currentUpdate.title}
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
            {currentUpdate.content}
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-secondary/30">
          <div className="flex items-center justify-between">
            {/* Pagination dots */}
            {updates.length > 1 && (
              <div className="flex gap-1.5">
                {updates.map((_, index) => (
                  <div 
                    key={index} 
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            )}
            
            <Button 
              onClick={handleNext}
              className="bg-primary text-primary-foreground hover:bg-primary/90 ml-auto"
              data-testid="update-next-btn"
            >
              {currentIndex < updates.length - 1 ? (
                <>
                  {language === 'fr' ? 'Suivant' : 'Next'}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              ) : (
                language === 'fr' ? 'Compris !' : 'Got it!'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
